import { Column, Row } from 'app/design/primitives/box';
import { Button, PressableType } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';
import type { TransactionMode } from 'app/types/listing';
import { useUISize } from 'app/utils/web-helpers';
import React from 'react';

export interface TransactionModeToggleOption {
  label: string;
  value: TransactionMode;
  disabled?: boolean;
}

interface TransactionModeToggleProps {
  options: TransactionModeToggleOption[];
  value: TransactionMode | null | undefined;
  onChange: (value: TransactionMode) => void;
  wrapOnSmallUI?: boolean;
}

export function TransactionModeToggle({ options, value, onChange, wrapOnSmallUI = true }: TransactionModeToggleProps) {
  const activeOptions = options.filter((option) => !option.disabled);
  const { isSmallUI } = useUISize();
  const mobileOptionRows = (() => {
    const rows: TransactionModeToggleOption[][] = [];
    for (let index = 0; index < activeOptions.length; index += 2) {
      rows.push(activeOptions.slice(index, index + 2));
    }
    return rows;
  })();

  const renderOption = (option: TransactionModeToggleOption) => {
    const isSelected = value === option.value;
    return (
      <Button
        key={option.value}
        flex={1}
        minHeight={52}
        minWidth={0}
        onPress={() => onChange(option.value)}
        pressableType={PressableType.GestureHandler}
        variant={isSelected ? 'primary' : 'secondary'}
        style={{ borderWidth: 0 }}
      >
        <Text color={isSelected ? 'surface.default' : 'text.primary'} flexShrink={1} minWidth={0} textAlign="center">
          {option.label}
        </Text>
      </Button>
    );
  };

  return (
    <Column
      backgroundColor="surface.primary-neutral"
      borderRadius="radius.lg"
      gap="spacing.general.x2"
      padding="spacing.general.x1"
      width="100%"
    >
      {isSmallUI && wrapOnSmallUI
        ? mobileOptionRows.map((row) => (
            <Row key={row.map((option) => option.value).join('-')} gap="spacing.general.x2">
              {row.map(renderOption)}
            </Row>
          ))
        : null}
      {!isSmallUI || !wrapOnSmallUI ? <Row gap="spacing.general.x2">{activeOptions.map(renderOption)}</Row> : null}
    </Column>
  );
}
